import { useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { retryCourseCompletion } from 'lib/reader';
import { uploadAllEntries } from 'lib/reader/analytics';
import { ContentBox } from 'components/common';
import { ReaderCompletionFormContent, ReaderCompletionFormResult } from '.';

import type { ReaderSettings } from 'shared-types';

const ReaderCompletionForm = () => {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    messages,
    pageState: {
      chapterSlug,
      completion,
      courseSlug,
      presentMode,
      readOnly,
      readerCourseData,
      settings,
      settingsPresentMode
    },
    theme
  } = useReduxState();

  const { toc } = readerCourseData;

  const { dispatch } = useDispatch();

  const handleRetry = () =>
    retryCourseCompletion({
      chapterSlug,
      courseSlug: courseSlug,
      dispatch,
      firstChapterSlug: toc.chapters[toc.chaptersOrder[0]].slug
    });

  const handleSubmit = async (values) => {
    try {
      const { password } = values;

      setError(null);
      setSubmitting(true);

      await uploadAllEntries();

      const response = await api.post(
        `/reader/courses/${courseSlug}/complete`,
        { password }
      );
      if (response.data && response.data.success) {
        dispatch.updatePageState({
          completion: response.data.completion,
          isCompleted: true
        });
        setSubmitting(false);
      } else if (response.data && response.data.error) {
        let errorMessage = messages.commonError;
        if (response.data.error === 'user_not_found') {
          errorMessage = messages.authInvalidPassword;
        } else if (response.data.error === 'account_locked') {
          errorMessage = messages.authAccountLocked;
        } else if (response.data.error === 'too_many_failed_logins') {
          errorMessage =
            `${messages.authTooManyFailedLoginsStart} ` +
            `${response.data.lockTemporarilyInMinutes} ` +
            messages.authTooManyFailedLoginsEnd;
        } else if (response.data.error === 'locked_after_failed_logins') {
          errorMessage = messages.authLockedAfterFailedLogins;
        }
        setError(errorMessage);
        setSubmitting(false);
      } else {
        throw new Error('error');
      }
    } catch (err) {
      setSubmitting(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const currentSettings =
    (presentMode ? settingsPresentMode : settings) || ({} as ReaderSettings);

  const hasNoCompletion = !completion || completion?.status === 'inprogress';

  return hasNoCompletion && readOnly ? null : (
    <div className="completion">
      <ContentBox size={currentSettings.fontSize}>
        <div className="content">
          {hasNoCompletion ? (
            <ReaderCompletionFormContent
              courseData={readerCourseData}
              error={error}
              font={currentSettings.font}
              fontSize={currentSettings.fontSize}
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          ) : (
            <ReaderCompletionFormResult
              completion={completion}
              fontSize={currentSettings.fontSize}
              onRetry={handleRetry}
            />
          )}
        </div>
      </ContentBox>

      <style jsx>{`
        .completion {
          margin: 50px 0 20px;
        }
        .content {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          padding: 25px;
        }
      `}</style>
    </div>
  );
};

export default ReaderCompletionForm;
